'use client'

import { createContext, useContext, useState } from 'react';
import { Package } from '@/types/package-type';
import { GET } from "@/api/get-request";
import React from 'react';
import Loading from '@/components/layout/page-loader';
import { Box } from '@mui/material';
import { patchPackagesResponse } from '@/utils/packages/package-patch';
import { useTranslations } from 'next-intl';
import packagesMock from '@/mocks/packages.mock.json';

interface PackageContextType {
    packages: Package[];
    loading: boolean;
    error: string | null;
    getPackages: () => Promise<void>;
    serviceFeatures: [];
    getServiceFeatures: () => Promise<void>;
}

const PackageContext = createContext<PackageContextType | undefined>(undefined);

export const usePackageContext = () => {
    const context = useContext(PackageContext);
    if (!context) {
        throw new Error('usePackageContext must be used within a PackageProvider');
    }
    return context;
};

export const PackageProvider = ({ children }: { children: React.ReactNode }) => {
    const [packages, setPackages] = useState<Package[]>([]);
    const [serviceFeatures, setServiceFeatures] = useState<[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const t = useTranslations('packages');

    // TODO: بعد از اصلاح بک‌اند این فلگ را false کنید یا حذف کنید
    const USE_MOCK_PACKAGES = true;

    const getPackages = async () => {
        try {
            let patchedResponse;

            if (USE_MOCK_PACKAGES) {
                // استفاده از mock محلی به‌جای بک‌اند
                patchedResponse = patchPackagesResponse(packagesMock as any, t);
            } else {
                const response = await GET({ url: `v1/packages` });
                if (!response.success) {
                    throw new Error('Failed to fetch packages');
                }

                // Apply patch to response (fixes colors, recommended flags, and internationalizes text)
                patchedResponse = patchPackagesResponse(response as any, t);
            }

            setPackages(patchedResponse.data);
            setLoading(false);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(() => {
        getPackages();
    }, []);

    const getServiceFeatures = async () => {
        try {
            const response = await GET({ url: `v1/enums/service_features` });
            if (!response.success) {
                throw new Error('Failed to fetch service features');
            }
            setServiceFeatures(response.data);
            setLoading(false);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    }

    return (
        <PackageContext.Provider value={{ packages, loading, error, getPackages, serviceFeatures, getServiceFeatures }}>
            {
                loading ?
                    <Box
                        height={'80vh'}
                    >
                        <Loading  />
                    </Box>
                    :
                    children
            }
        </PackageContext.Provider>
    );
};
